<script lang="ts" setup>
import { useCurrentUser } from "@/api/current-user";
import { DrawerViewType, useDrawerStore } from "@/stores";
import { useFeaturesStore } from "@/stores/features";
import { useScopeStore } from "@/stores/scope";
import { mdiClose, mdiFormatListBulleted, mdiHeart, mdiSelectionSearch } from "@mdi/js";
import {
	Button,
	DataCollectionActiveFilters,
	DataCollectionFilterMenu,
	Icon,
	InputGroup,
	makeFacetedMapping,
	Progress,
	Tabs,
	TabsList,
	TabTrigger,
	TextInput,
} from "@soft-db/ui";
import { storeToRefs } from "pinia";
import { useDrawerCollection } from "./composables/drawer-collection";
import Content from "./Content.vue";
import { DrawerSelection, ItemObject, MonitoringObject } from "./use-drawer-data";

const emit = defineEmits(["click"]);
const props = defineProps({
	objects: { type: Array as PropType<MonitoringObject[]>, default: () => [] },
	headers: Boolean,
});

const { isPublic } = useCurrentUser();

const { hasFeature } = useFeaturesStore();
const { isScope } = useScopeStore();

const objects = computed<MonitoringObject[]>(() => {
	let objects: MonitoringObject[];
	if (props.objects.length === 0) {
		objects = ["site", "station"];
	} else {
		objects = props.objects;
	}

	return objects.filter((obj) => {
		// Remove stations when in space
		if (obj === "station" && isScope("space")) return false;
		return hasFeature(obj);
	});
});

const useTabs = computed(() => objects.value.includes("site") || objects.value.includes("station"));

const { t } = useI18n();

const filterInputRef = useTemplateRef("$filterInputRef");
function removeSearch() {
	state.filter = "";
	filterInputRef.value?.$el.focus();
}

const { state, items, isLoading } = useDrawerCollection(objects);

const hasGlobalFilter = computed(() => state.filter !== "");
const hasFieldFilters = computed(() => state.fieldFilters.length !== 0);

const hasFilters = logicOr(hasGlobalFilter, hasFieldFilters);

function clearFilters() {
	state.filter = "";
	state.fieldFilters = [];
}

const toolbarMapping = computed(() => {
	return makeFacetedMapping<ItemObject>({
		object: {
			name: t("audit.objectType"),
			allowMultiple: true,
			choices: {
				site: {
					name: t("object.site", 2),
				},
				station: {
					name: t("object.station", 2),
				},
				space: {
					name: t("object.space", 2),
				},
			},
		},

		status: {
			name: t("common.status"),
			allowMultiple: false,
			choices: {
				online: {
					name: t("live-status.online"),
				},
				waiting: {
					name: t("live-status.idle"),
				},
				"recently-offline": {
					name: t("live-status.recentlyOffline"),
					hint: t("live-status.recentlyOfflineHelp"),
				},
				offline: {
					name: t("live-status.offline"),
				},
				none: {
					name: t("common.notAvailable"),
				},
			},
		},

		connected: {
			name: t("common.linked"),
			allowMultiple: false,
			hint: t("common.linkedHelp"),
			choices: {
				true: {
					name: t("common.linked"),
				},
				false: {
					name: t("common.notLinked"),
				},
			},
		},
		spaces: {
			name: t("object.space"),
			allowMultiple: true,
		},
	});
});

const { view, favoriteEnabled } = storeToRefs(useDrawerStore());

function itemSelect(selection: DrawerSelection) {
	emit("click", selection);
}
</script>

<template>
	<div class="bg-base-200 flex h-full w-72 flex-col gap-1 px-2 pt-2">
		<div v-if="!isPublic">
			<div class="flex w-full items-center gap-1">
				<InputGroup class="flex w-full grow items-center">
					<TextInput
						ref="$filterInputRef"
						v-model="state.filter"
						:placeholder="t('common.filter') + '...'"
						size="sm"
					/>
					<Button shape="round" size="xs" variant="ghost" @click="removeSearch">
						<Icon :d="mdiClose" />
					</Button>
				</InputGroup>
				<DataCollectionFilterMenu :options="toolbarMapping" />
			</div>

			<div class="mt-2">
				<DataCollectionActiveFilters compact :options="toolbarMapping" :maxCount="2" />
			</div>
		</div>
		<Tabs v-if="favoriteEnabled && useTabs" v-model="view" class="flex w-full">
			<TabsList class="w-full">
				<TabTrigger
					:value="DrawerViewType.FAVORITES"
					class="aria-selected:text-primary flex-1 gap-1"
				>
					<Icon :d="mdiHeart" />
					<span>{{ t("common.favorite", 2) }}</span>
				</TabTrigger>
				<TabTrigger :value="DrawerViewType.ALL" class="aria-selected:text-primary flex-1 gap-1">
					<Icon :d="mdiFormatListBulleted" />
					<span>{{ t("common.all") }}</span>
				</TabTrigger>
			</TabsList>
		</Tabs>
		<div v-if="isLoading" class="w-full pb-2">
			<Progress class="w-full" color="primary" />
		</div>
		<Content
			v-else-if="items.length !== 0"
			:header="headers"
			:collection="items.map((e) => e.item)"
			@select="itemSelect"
		/>
		<div v-else class="mt-4 flex w-full flex-col items-center justify-center">
			<template v-if="hasFilters">
				<Icon class="text-muted size-12" :d="mdiSelectionSearch"></Icon>
				<div>{{ t("table.noMatchingResult") }}</div>
				<Button class="h-fit" size="sm" variant="link" color="primary" @click="clearFilters">
					{{ t("table.clearFilters") }}
				</Button>
			</template>
			<template v-else>
				{{ t("table.noData") }}
			</template>
		</div>
	</div>
</template>
